// Calls to the PM C3 server functions (/api/*). Every change to data goes through here.
import { supabase } from './supabase';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function token(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const t = data.session?.access_token;
  if (!t) throw new ApiError(401, 'Please sign in again');
  return t;
}

export async function api<T = unknown>(path: string, body?: unknown, method = 'POST'): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    headers: {
      authorization: `Bearer ${await token()}`,
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* empty */
  }
  if (!res.ok) {
    const msg = (data as { error?: string } | null)?.error ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export type WriteOp = 'insert' | 'update' | 'delete' | 'restore';

export async function write<T = Record<string, unknown>>(
  table: string,
  op: WriteOp,
  opts: { id?: string; values?: Record<string, unknown>; summary?: string; batch_id?: string } = {},
): Promise<T> {
  const r = await api<{ row: T }>('write', { table, op, ...opts });
  return r.row;
}

export type UndoResult = {
  ok: boolean;
  undone?: number;
  conflicts?: { audit_id: number; table: string; row_id: string }[];
};

export function undo(opts: { audit_id?: number; batch_id?: string; force?: boolean }) {
  return api<UndoResult>('undo', opts);
}

export async function downloadFile(id: string) {
  const r = await api<{ url: string; name: string; link?: boolean }>(`download?id=${encodeURIComponent(id)}`, undefined, 'GET');
  if (r.link) {
    window.open(r.url, '_blank', 'noopener');
    return;
  }
  const a = document.createElement('a');
  a.href = r.url;
  a.download = r.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** A short-lived link the in-page viewer can show (PDFs, photos). */
export function viewUrl(id: string) {
  return api<{ url: string; name: string; mime: string | null; link?: boolean }>(`download?id=${encodeURIComponent(id)}&inline=1`, undefined, 'GET');
}

/** A PDF (or other file) made by a server function, as a blob URL for the viewer / a new tab. */
export async function apiBlob(path: string): Promise<{ url: string; name: string }> {
  const res = await fetch(`/api/${path}`, { headers: { authorization: `Bearer ${await token()}` } });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      msg = ((await res.json()) as { error?: string }).error ?? msg;
    } catch {
      /* empty */
    }
    throw new ApiError(res.status, msg);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'document.pdf';
  return { url: URL.createObjectURL(await res.blob()), name };
}
